import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AssignableDocumentType } from '@/features/document-templates/types/documentTemplates.types';
import {
    generateDocumentSchema,
    type GenerateDocumentFormInput,
    type GenerateDocumentFormValues,
} from '../schemas/generateDocument.schema';
import { isJustificationRequired } from '../utils/accessRefusal';
import { mapGenerateDocumentError } from '../utils/mapGenerateDocumentError';
import { useGenerateDocument } from './useGenerateDocument';

/**
 * The generate modal's form. A justification refusal closes the modal: the access prompt
 * that the refusal watcher raises is the answer, and it would otherwise sit behind the modal.
 */
export function useGenerateDocumentForm(agreementId: string | undefined, onDone: () => void) {
    const mutation = useGenerateDocument(agreementId);
    const [formError, setFormError] = useState<string | null>(null);
    const {
        handleSubmit,
        reset,
        setError,
        setValue,
        watch,
        formState: { errors },
    } = useForm<GenerateDocumentFormInput, unknown, GenerateDocumentFormValues>({
        resolver: zodResolver(generateDocumentSchema),
        mode: 'onBlur',
    });

    const finish = () => {
        reset();
        setFormError(null);
        onDone();
    };

    const submit = handleSubmit((values) => {
        setFormError(null);
        mutation.mutate(values.documentType, {
            onSuccess: finish,
            onError: (error: unknown) => {
                if (agreementId && isJustificationRequired(error, agreementId)) {
                    finish();
                    return;
                }
                const mapped = mapGenerateDocumentError(error);
                if (mapped.fieldMessage) setError('documentType', { message: mapped.fieldMessage });
                setFormError(mapped.message);
            },
        });
    });

    return {
        documentType: (watch('documentType') ?? '') as AssignableDocumentType | '',
        fieldError: errors.documentType?.message,
        formError: formError ?? undefined,
        isSubmitting: mutation.isPending,
        setDocumentType: (value: AssignableDocumentType) => setValue('documentType', value, { shouldValidate: true }),
        submit: () => void submit(),
        discard: () => {
            reset();
            setFormError(null);
        },
    };
}
